import type StoryTree from '@/model/StoryTree'
import MapInfo from '@/model/MapInfo'

/** 根节点：与 StoryTree.createStoryTree 一致 */
function isRootMap(map: MapInfo, allIds: Set<number>): boolean {
  return map.ParentId <= 0 || map.ParentId === map.Id || !allIds.has(map.ParentId)
}

/** 归一化「直接父 id」：根层为 0 */
function directParentKey(map: MapInfo, allIds: Set<number>): number {
  return isRootMap(map, allIds) ? 0 : map.ParentId
}

function listSiblingsOfParent(maps: MapInfo[], parentKey: number, allIds: Set<number>): MapInfo[] {
  return maps
    .filter((m) => {
      if (parentKey === 0) return isRootMap(m, allIds)
      return m.ParentId === parentKey
    })
    .sort((a, b) => (a.Order !== b.Order ? a.Order - b.Order : a.Id - b.Id))
}

/** 生成下一个可用剧情 ID。 */
export function nextAvailableMapId(maps: MapInfo[]): number {
  let max = 0
  for (const m of maps) {
    if (m.Id > max) max = m.Id
  }
  return max + 1
}

/** 判断一个剧情是否是另一个剧情的真子孙节点。 */
export function isStrictDescendantOf(st: StoryTree, ancestorId: number, maybeDescId: number): boolean {
  if (ancestorId === maybeDescId) return false
  st.createStoryTree()
  const byId = new Map(st.Maps.map((m) => [m.Id, m]))
  const root = byId.get(ancestorId)
  if (!root) return false
  function dfs(m: MapInfo): boolean {
    for (const c of m.Childs ?? []) {
      if (c.Id === maybeDescId) return true
      if (dfs(c)) return true
    }
    return false
  }
  return dfs(root)
}

/** 深度优先展开 Maps，保证序列化顺序稳定 */
export function reorderMapsDepthFirst(st: StoryTree): void {
  st.createStoryTree()
  const out: MapInfo[] = []
  function walk(nodes: MapInfo[]) {
    const sorted = [...nodes].sort((a, b) => (a.Order !== b.Order ? a.Order - b.Order : a.Id - b.Id))
    for (const n of sorted) {
      out.push(n)
      if (n.Childs?.length) walk(n.Childs)
    }
  }
  walk(st.rootMaps)
  st.Maps = out
}

function collectSubtreeIds(st: StoryTree, rootId: number): Set<number> {
  st.createStoryTree()
  const byId = new Map(st.Maps.map((m) => [m.Id, m]))
  const root = byId.get(rootId)
  const out = new Set<number>()
  if (!root) {
    out.add(rootId)
    return out
  }
  function dfs(m: MapInfo) {
    out.add(m.Id)
    for (const c of m.Childs ?? []) dfs(c)
  }
  dfs(root)
  return out
}

/** 删除该剧情及子剧情后是否仍至少保留一条剧情。 */
export function canDeleteMapCascade(st: StoryTree, id: number): boolean {
  const ids = collectSubtreeIds(st, id)
  return st.Maps.length > ids.size
}

/** 级联删除剧情及其全部子剧情，同时清理对应事件桶。 */
export function deleteMapCascade(st: StoryTree, id: number): boolean {
  if (!canDeleteMapCascade(st, id)) return false
  const ids = collectSubtreeIds(st, id)
  st.Maps = st.Maps.filter((m) => !ids.has(m.Id))
  for (const i of ids) {
    delete st.Events[String(i)]
  }
  st.createStoryTree()
  reorderMapsDepthFirst(st)
  return true
}

/**
 * 在选中剧情后追加同级剧情（与选中项同一 ParentId；无选中则加在根层末尾）。
 */
export function addStoryMapSiblingAfter(st: StoryTree, afterId: number | null): MapInfo {
  const nextId = nextAvailableMapId(st.Maps)
  const allIds = new Set(st.Maps.map((m) => m.Id))

  let parentKey = 0
  let insertIndex = 0

  if (afterId != null) {
    const rel = st.Maps.find((m) => m.Id === afterId)
    if (rel) {
      parentKey = directParentKey(rel, allIds)
      const sibs = listSiblingsOfParent(st.Maps, parentKey, allIds)
      const idx = sibs.findIndex((m) => m.Id === afterId)
      insertIndex = idx >= 0 ? idx + 1 : sibs.length
    } else {
      parentKey = 0
      insertIndex = listSiblingsOfParent(st.Maps, 0, allIds).length
    }
  } else {
    parentKey = 0
    insertIndex = listSiblingsOfParent(st.Maps, 0, allIds).length
  }

  const nm = new MapInfo({
    name: `剧情${nextId}`,
    id: nextId,
    parentId: parentKey === 0 ? 0 : parentKey,
    order: 0,
    list: [],
  })

  const sibs = listSiblingsOfParent(st.Maps, parentKey, allIds)
  const merged = [...sibs.slice(0, insertIndex), nm, ...sibs.slice(insertIndex)]
  merged.forEach((m, i) => {
    m.Order = i
  })
  st.Maps.push(nm)
  st.Events[String(nextId)] = []
  reorderMapsDepthFirst(st)
  st.createStoryTree()
  return nm
}

/** 在指定父剧情下追加子剧情。 */
export function addStoryMapChild(st: StoryTree, parentId: number): MapInfo | null {
  const parent = st.Maps.find((m) => m.Id === parentId)
  if (!parent) return null
  const nextId = nextAvailableMapId(st.Maps)
  const allIds = new Set(st.Maps.map((m) => m.Id))
  const siblings = listSiblingsOfParent(st.Maps, parentId, allIds)
  const nm = new MapInfo({
    name: `剧情${nextId}`,
    id: nextId,
    parentId,
    order: siblings.length,
    list: [],
  })
  st.Maps.push(nm)
  st.Events[String(nextId)] = []
  reorderMapsDepthFirst(st)
  st.createStoryTree()
  return nm
}

/** 修改剧情名称。 */
export function renameStoryMap(st: StoryTree, id: number, name: string): boolean {
  const target = st.Maps.find((m) => m.Id === id)
  const nextName = name.trim()
  if (!target || !nextName) return false
  target.Name = nextName
  st.createStoryTree()
  return true
}

/** 根据拖拽目标重排剧情树中的父子关系和同级顺序。 */
export function applyStoryMapDrop(
  st: StoryTree,
  dragId: number,
  targetId: number,
  dropPosition: 'before' | 'inside' | 'after',
): boolean {
  if (dragId === targetId) return false
  const byId = new Map(st.Maps.map((m) => [m.Id, m]))
  const drag = byId.get(dragId)
  const target = byId.get(targetId)
  if (!drag || !target) return false
  const allIds = new Set(st.Maps.map((m) => m.Id))

  if (dropPosition === 'inside') {
    if (isStrictDescendantOf(st, dragId, targetId)) return false
  }

  let newParentKey: number
  if (dropPosition === 'inside') {
    newParentKey = targetId
    drag.ParentId = targetId
  } else {
    newParentKey = directParentKey(target, allIds)
    drag.ParentId = newParentKey === 0 ? 0 : newParentKey
  }

  const sibsWithoutDrag = listSiblingsOfParent(st.Maps, newParentKey, allIds).filter((m) => m.Id !== dragId)

  if (dropPosition === 'inside') {
    const merged = [...sibsWithoutDrag, drag]
    merged.forEach((m, i) => {
      m.Order = i
    })
  } else {
    const ti = sibsWithoutDrag.findIndex((m) => m.Id === targetId)
    if (ti < 0) return false
    const ins = dropPosition === 'before' ? ti : ti + 1
    const merged = [...sibsWithoutDrag.slice(0, ins), drag, ...sibsWithoutDrag.slice(ins)]
    merged.forEach((m, i) => {
      m.Order = i
    })
  }

  reorderMapsDepthFirst(st)
  st.createStoryTree()
  return true
}
